/**
 * HelpBubble — petit « ? » contextuel posable partout (Lot H.4).
 *
 * Ouvre l'article d'aide lié dans un `Modal lg` EN OVERLAY, sans quitter l'écran
 * courant : le vigneron interrompu ne perd pas sa saisie (cf 04-ux § Surface 3,
 * terrain n°1). L'article n'est chargé qu'à l'ouverture (`enabled`) → une bulle
 * non cliquée ne coûte aucune requête, même si l'écran en pose plusieurs.
 *
 * Cible tactile 44px min (h-11 w-11). Exemple d'usage : cf `AidePage`.
 */

import { Modal, Skeleton } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";
import { useState } from "react";

import { trpc } from "../../lib/trpc.js";

import { MarkdownRenderer } from "./MarkdownRenderer.js";

export interface HelpBubbleProps {
  /** Slug de l'article d'aide à ouvrir (deep-link). */
  articleSlug: string;
  /** Libellé accessible du bouton (ex. « Aide : Dosage »). */
  label?: string;
}

export function HelpBubble({ articleSlug, label }: HelpBubbleProps) {
  const [ouvert, setOuvert] = useState(false);

  const {
    data: article,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["help.getBySlug", articleSlug],
    queryFn: () => trpc.help.getBySlug.query({ slug: articleSlug }),
    enabled: ouvert,
  });

  return (
    <>
      <button
        data-workspace-icon-action=""
        type="button"
        onClick={() => setOuvert(true)}
        aria-label={label ?? "Ouvrir l'aide"}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-champagne-500 transition hover:bg-champagne-50 hover:text-champagne-700"
      >
        <HelpCircle size={18} aria-hidden="true" />
      </button>

      <Modal
        open={ouvert}
        onClose={() => setOuvert(false)}
        size="lg"
        title={article?.titre ?? "Aide"}
      >
        {isLoading ? (
          <Skeleton variant="text" lines={5} />
        ) : isError || !article ? (
          <p className="text-[14px] text-champagne-700">
            Cet article d'aide n'est pas disponible pour le moment.
          </p>
        ) : (
          <MarkdownRenderer contenu={article.contenu_md} />
        )}
      </Modal>
    </>
  );
}
